import React, { useEffect, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import client from '../../api/client';
import Analytics from '../../utils/Analytics';
import { Link } from 'react-router-dom';

export default function ContactForm({ modal = false }) {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: '',
		message: '',
		consent: false,
	});
	const { setAlert } = useAlert();
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(null);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: name === 'consent' ? !prevData.consent : value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.consent)
			return setAlert('error', 'Please agree to the terms and conditions');
		if (!formData.name || !formData.email || !formData.message)
			return setAlert('error', 'Please fill out all required fields');

		if (window?.gtag) {
			window.gtag('event', 'form_submit', {
				event_category: 'formSubmit:contact',
				event_label: 'Contact Form Submitted',
			});
		}
		setSubmitting(true);
		try {
			await client.post('/message', formData);
			setSuccess(true);
			setAlert('success', 'Message sent successfully!');

			Analytics.pageViewEvent('Contact:FormSubmit', 'Contact Form Submitted');
			setFormData({
				name: '',
				email: '',
				phone: '',
				message: '',
				consent: false,
			});
		} catch (err) {
			setError(err.message || 'Something went wrong.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleBlur = (e) => {
		e.preventDefault();
		console.log('handleBlur');
		if (window?.gtag) {
			window.gtag('event', 'form_submit', {
				event_category: 'formSubmit:interaction:blur',
				event_label: 'Contact Form Interaction',
			});
		}
	};
	useEffect(() => {
		let fired = false;
		const checkFormInView = () => {
			// calculate where form is on page
			const form = document.querySelector('#contact-form');
			const formRect = form.getBoundingClientRect();
			const formTop = formRect.top;
			const formHeight = formRect.height;
			const formBottom = formTop + formHeight;

			const formInView = formTop < window.innerHeight && formBottom > 0;
			if (formInView) {
				console.log('form is in view');
				if (window?.gtag && !fired) {
					fired = true;
					window.gtag('event', 'form_submit', {
						event_category: 'formSubmit:interaction:scroll:inView',
						event_label: 'Contact Form Interaction',
					});
				}
			}
		};
		window.addEventListener('scroll', checkFormInView);
		return () => window.removeEventListener('scroll', checkFormInView);
	}, []);
	return (
		<div
			className={`${
				!modal ? 'lg:pt-8' : ''
			} max-w-screen-md mx-auto max-h-screen overflow-auto bg-white rounded-lg shadow-lg`}
		>
			<form
				onSubmit={handleSubmit}
				id='contact-form'
				className='px-6 pb-5 pt-8 sm:pb-10 lg:px-8 lg:py-12'
			>
				<div className='mx-auto max-w-xl'>
					<div className='grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2'>
						<div>
							<label
								htmlFor='name'
								className='block text-sm font-semibold text-gray-900'
							>
								Name
							</label>
							<input
								onChange={handleChange}
								type='text'
								name='name'
								value={formData.name}
								onBlur={handleBlur}
								id='name'
								autoComplete='given-name'
								className='mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-primary-600'
							/>
						</div>
						<div>
							<label
								htmlFor='email'
								className='block text-sm font-semibold text-gray-900'
							>
								Email
							</label>
							<input
								onChange={handleChange}
								type='email'
								name='email'
								onBlur={handleBlur}
								value={formData.email}
								id='email'
								autoComplete='email'
								className='mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-primary-600'
							/>
						</div>
						<div>
							<label
								htmlFor='phone'
								className='block text-sm font-semibold text-gray-900'
							>
								Phone (Optional)
							</label>
							<input
								onChange={handleChange}
								type='tel'
								onBlur={handleBlur}
								name='phone'
								value={formData.phone}
								id='phone'
								autoComplete='tel'
								className='mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-primary-600'
							/>
						</div>
						<div className='sm:col-span-2'>
							<label
								htmlFor='message'
								className='block text-sm font-semibold text-gray-900'
							>
								Message
							</label>
							<textarea
								id='message'
								name='message'
								onChange={handleChange}
								onBlur={handleBlur}
								value={formData.message}
								rows={4}
								className='mt-2.5 block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-2 focus:ring-primary-600'
							/>
						</div>
						<div className='sm:col-span-2 flex items-start pt-3'>
							<input
								id='consent'
								name='consent'
								type='checkbox'
								onBlur={handleBlur}
								checked={formData.consent}
								onChange={handleChange}
								className='h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-600'
							/>
							<label
								htmlFor='consent'
								className='ml-3 text-sm text-gray-500'
							>
								By checking this box, I agree to the{' '}
								<Link
									to='/terms'
									className='text-primary-600 hover:text-primary-800'
								>
									terms & conditions
								</Link>
								.
							</label>
						</div>
						<div className='mt-8 flex'>
							<button
								type='submit'
								className='rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:ring-2 focus:ring-primary-600'
							>
								{!submitting ? 'Submit' : 'Submitting...'}
							</button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
}
